/**
 * Hook for price calculation logic
 * Extracted from DeviceSpecifications.tsx
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { calculateOffer } from '../../../lib/calculateOffer';
import { PRICE_CALCULATION_DEBOUNCE_MS } from '../constants';
import { buildOfferRequestPayload } from '../utils/buildOfferPayload';
import type { CalculateOfferRequest } from '../../../lib/calculateOffer';

export interface UsePriceCalculationParams {
  modelName: string;
  selectedStorage: string;
  answers: Record<string, string>;
  hasDynamicQuestions: boolean;
  isIPhone: boolean;
  legacyState?: {
    selectedCondition?: string;
    selectedFunctionality?: string;
    selectedScreenCondition?: string;
    selectedHousingCondition?: string;
    selectedBatteryCapacity?: string;
    selectedNetworkType?: string;
    selectedBattery?: string;
  };
  debounceMs?: number;
  auto?: boolean;
}

// Helper to create a stable string key from answers/legacyState for comparison
function createStableKey(
  modelName: string,
  selectedStorage: string,
  answers: Record<string, string>,
  legacyState?: UsePriceCalculationParams['legacyState']
): string {
  const answerKeys = Object.keys(answers).sort();
  const answerPart = answerKeys.map(k => `${k}:${answers[k]}`).join('|');

  const legacyPart = legacyState
    ? [
        legacyState.selectedCondition,
        legacyState.selectedFunctionality,
        legacyState.selectedScreenCondition,
        legacyState.selectedHousingCondition,
        legacyState.selectedBatteryCapacity,
        legacyState.selectedNetworkType,
        legacyState.selectedBattery,
      ].join('|')
    : '';

  return `${modelName}|${selectedStorage}|${answerPart}|${legacyPart}`;
}

/**
 * Hook to manage price calculation
 *
 * @param params - Configuration object for price calculation
 * @returns Object with price, loading state, error state, and calculatePrice function
 */
export function usePriceCalculation({
  modelName,
  selectedStorage,
  answers,
  hasDynamicQuestions,
  isIPhone,
  legacyState,
  debounceMs = PRICE_CALCULATION_DEBOUNCE_MS,
  auto = true,
}: UsePriceCalculationParams) {
  const [price, setPrice] = useState<number | null>(null);
  const [isPricing, setIsPricing] = useState<boolean>(false);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [isOnRequest, setIsOnRequest] = useState<boolean>(false);
  const [settledKey, setSettledKey] = useState<string | null>(null);

  const currentKey = `${createStableKey(modelName, selectedStorage, answers, legacyState)}|${hasDynamicQuestions}|${isIPhone}`;
  const currentKeyRef = useRef(currentKey);
  currentKeyRef.current = currentKey;
  const requestIdRef = useRef(0);

  // Use refs to store current values for the async callback
  const answersRef = useRef(answers);
  const legacyStateRef = useRef(legacyState);
  const hasDynamicQuestionsRef = useRef(hasDynamicQuestions);
  const isIPhoneRef = useRef(isIPhone);

  // Keep refs in sync
  answersRef.current = answers;
  legacyStateRef.current = legacyState;
  hasDynamicQuestionsRef.current = hasDynamicQuestions;
  isIPhoneRef.current = isIPhone;

  const calculatePrice = useCallback(
    async (payloadOverride?: CalculateOfferRequest | null) => {
      // A changed answer must get its own calculation, even if an older one
      // is still running. Only the latest request for these inputs may settle.
      const requestId = ++requestIdRef.current;
      const isCurrentRequest = () =>
        requestId === requestIdRef.current && currentKey === currentKeyRef.current;

      // Build payload - storage is OPTIONAL, will use 'default' if not provided
      const payload =
        payloadOverride ??
        buildOfferRequestPayload(
          modelName,
          selectedStorage, // Can be empty string - buildOfferRequestPayload handles fallback
          answersRef.current,
          hasDynamicQuestionsRef.current,
          isIPhoneRef.current,
          legacyStateRef.current
        );

      if (!payload) {
        // Only happens if modelName is missing
        setPrice(null);
        setIsOnRequest(false);
        setPriceError(null);
        setIsPricing(false);
        setSettledKey(currentKey);
        return;
      }

      console.log('[usePriceCalculation] Calculating price for:', payload.device_model_key);
      setPrice(null);
      setIsOnRequest(false);
      setPriceError(null);
      setIsPricing(true);

      try {
        const res = await calculateOffer(payload);
        if (!isCurrentRequest()) return;

        if (!res) {
          setPrice(null);
          setIsOnRequest(false);
        } else {
          // ALWAYS set isOnRequest from the response, even when final_price is null
          // This allows the user to proceed with "On Request" items
          setIsOnRequest(res.is_on_request || false);

          // Set price - can be null for On Request items, that's OK
          if (res.final_price != null) {
            setPrice(res.final_price);
          } else {
            // On Request with no price: set to null but allow flow to continue
            setPrice(null);
          }

          console.log('[usePriceCalculation] Result:', {
            price: res.final_price,
            isOnRequest: res.is_on_request,
          });
        }

        setPriceError(null);
      } catch (err: any) {
        if (!isCurrentRequest()) return;
        console.error('[usePriceCalculation] Error:', err?.message || err);
        setPrice(null);
        setIsOnRequest(false);
        const errorCode = err?.code || 'pricing_failed';
        setPriceError(errorCode === 'NOT_FOUND' ? 'not_found' : errorCode);
      } finally {
        if (isCurrentRequest()) {
          setSettledKey(currentKey);
          setIsPricing(false);
        }
      }
    },
    [modelName, selectedStorage, currentKey]
  );

  // Auto-calculate when inputs change
  // NOTE: selectedStorage is OPTIONAL - calculation proceeds with 'default' if empty
  useEffect(() => {
    setPrice(null);
    setIsOnRequest(false);
    setPriceError(null);
    setSettledKey(null);
    setIsPricing(auto && !!modelName);

    const timeoutId = auto && modelName
      ? window.setTimeout(() => { void calculatePrice(); }, debounceMs)
      : undefined;

    return () => {
      window.clearTimeout(timeoutId);
      // Also invalidate in-flight responses on input changes and unmount.
      requestIdRef.current++;
    };
  }, [auto, debounceMs, modelName, calculatePrice]);

  // Invalidate during render as well: the sell button must stay disabled from
  // the moment an answer changes, including the debounce before the request.
  const isCurrentResult = settledKey === currentKey;
  return {
    price: isCurrentResult ? price : null,
    isPricing: isPricing || (auto && !!modelName && !isCurrentResult),
    priceError: isCurrentResult ? priceError : null,
    isOnRequest: isCurrentResult && isOnRequest,
    calculatePrice,
  };
}
